import { normalizeLatexExpression } from './normalize';

export const LATEX_COMMAND_SOURCE = String.raw`\\[a-zA-Z]+`;

export const MATH_DELIMITER_POLICY = {
  allowSingleDollar: false,
} as const;

export const MATH_DISPLAY_POLICY = {
  inlineExpressionMaxLength: 50,
  nakedInlineEnvironments: ['math'],
  nakedBlockEnvironments: ['displaymath', 'eqnarray', 'eqnarray*', 'flalign', 'flalign*', 'multline', 'multline*', 'cases'],
} as const;

export const inferMathDisplay = (expression: string): 'inline' | 'block' =>
  expression.includes('\n') ||
  expression.includes('\\begin{') ||
  expression.includes('\\[') ||
  expression.length > MATH_DISPLAY_POLICY.inlineExpressionMaxLength
    ? 'block'
    : 'inline';

export type MathTextSegment = { type: 'text'; value: string } | { type: 'math'; expression: string; display: 'inline' | 'block' };

/** A delimited expression and its position in the original text. */
export interface DelimitedMathExpression {
  content: string;
  start: number;
  end: number;
  delimiter: '$$' | '$';
  isBlock: boolean;
}

export interface MathExpressionOptions {
  allowSingleDollar?: boolean;
  singleDollarMaxLength?: number;
}

export type DelimitedMathTextSegment =
  | { type: 'text'; value: string }
  | { type: 'math'; expression: string; raw: string; delimiter: '$$' | '$'; display: 'inline' | 'block' };

const findDelimiter = (value: string, delimiter: string, start: number): number => {
  let index = value.indexOf(delimiter, start);

  while (index !== -1) {
    let backslashes = 0;
    for (let cursor = index - 1; cursor >= 0 && value[cursor] === '\\'; cursor--) backslashes++;
    if (backslashes % 2 === 0) return index;
    index = value.indexOf(delimiter, index + delimiter.length);
  }

  return -1;
};

export const parseMathText = (value: string): MathTextSegment[] => {
  const segments: MathTextSegment[] = [];
  const delimiters = MATH_DELIMITER_POLICY.allowSingleDollar ? ['$$$', '$$', '$'] : ['$$$', '$$'];
  let cursor = 0;

  while (cursor < value.length) {
    const candidates = delimiters
      .map((delimiter) => ({ delimiter, index: findDelimiter(value, delimiter, cursor) }))
      .filter(({ index }) => index !== -1)
      .sort((a, b) => a.index - b.index || b.delimiter.length - a.delimiter.length);
    const opening = candidates[0];
    if (!opening) break;

    const contentStart = opening.index + opening.delimiter.length;
    const closingIndex = findDelimiter(value, opening.delimiter, contentStart);
    if (closingIndex === -1) break;

    const rawExpression = value.slice(contentStart, closingIndex);
    const expression = normalizeLatexExpression(rawExpression);
    if (!expression) {
      break;
    }

    if (opening.index > cursor) segments.push({ type: 'text', value: value.slice(cursor, opening.index) });
    segments.push({ type: 'math', expression, display: opening.delimiter === '$$$' ? 'inline' : inferMathDisplay(rawExpression) });
    cursor = closingIndex + opening.delimiter.length;
  }

  if (cursor < value.length) segments.push({ type: 'text', value: value.slice(cursor) });
  return segments.length > 0 ? segments : [{ type: 'text', value }];
};

export const containsMath = (value: string): boolean => parseMathText(value).some((segment) => segment.type === 'math');

/** Anything that marks a span as TeX rather than prose: a command, a script, or a group. */
const LATEX_SIGNAL = new RegExp(`${LATEX_COMMAND_SOURCE}|[\\^_{}]`);
const isDigit = (ch: string | undefined) => ch !== undefined && ch >= '0' && ch <= '9';

/**
 * Is this `$…$` span two currency amounts rather than one expression? True only when the opening
 * `$` is followed by a digit, the closing `$` is ALSO followed by a digit (so it is really the
 * next amount's opener), and the span carries no TeX signal at all.
 */
const isCurrencyRun = (text: string, openIndex: number, closeIndex: number, content: string): boolean =>
  isDigit(text[openIndex + 1]) && isDigit(text[closeIndex + 1]) && !LATEX_SIGNAL.test(content);

/** Extracts single- and double-dollar expressions with deterministic position tracking. */
export const extractMathExpressions = (text: string, options: MathExpressionOptions = {}): DelimitedMathExpression[] => {
  const { allowSingleDollar = true, singleDollarMaxLength = 200 } = options;
  const expressions: DelimitedMathExpression[] = [];
  let index = 0;

  while (index < text.length) {
    if (text[index] !== '$') {
      index++;
      continue;
    }

    if (text[index + 1] === '$') {
      const start = index;
      index += 2;
      const isBlock = text[index] === '\n';
      const contentStart = isBlock ? index + 1 : index;
      let closeIndex = -1;

      for (let cursor = contentStart; cursor < text.length; cursor++) {
        if (isBlock) {
          if (text[cursor] === '\n' && text[cursor + 1] === '$' && text[cursor + 2] === '$') {
            closeIndex = cursor;
            break;
          }
        } else if (text[cursor] === '$' && text[cursor + 1] === '$') {
          closeIndex = cursor;
          break;
        }
      }

      if (closeIndex === -1) {
        index = start + 2;
        continue;
      }

      const content = text.slice(contentStart, closeIndex).trim();
      const end = closeIndex + 2;
      if (content) expressions.push({ content, start, end, delimiter: '$$', isBlock });
      index = end;
      continue;
    }

    if (!allowSingleDollar) {
      index++;
      continue;
    }

    const start = index++;
    const maxSearch = Math.min(index + singleDollarMaxLength, text.length);
    let closeIndex = -1;
    for (let cursor = index; cursor < maxSearch; cursor++) {
      if (text[cursor] === '\n') break;
      if (text[cursor] === '$') {
        closeIndex = cursor;
        break;
      }
    }

    if (closeIndex === -1) continue;

    const content = text.slice(index, closeIndex).trim();
    // Two currency amounts in one sentence look exactly like one `$…$` expression: "lunch is $5
    // and you pay with $10" was parsed as the math `5 and you pay with`, so the prose vanished
    // into an equation and both amounts lost their sign. Money problems are everywhere in K-12
    // math, and this grammar feeds the editor, read-aloud, and every export path.
    //
    // The tell is that BOTH delimiters hug a digit — the closing `$` is really the next amount's
    // opening one — with nothing inside that looks like TeX. `$3$` stays math (its closer is not
    // followed by a digit), and anything containing a command, script, or group stays math too.
    if (isCurrencyRun(text, start, closeIndex, content)) continue;
    const end = closeIndex + 1;
    if (content) expressions.push({ content, start, end, delimiter: '$', isBlock: false });
    index = end;
  }

  return expressions;
};

/** Extracts only double-dollar expression contents. */
export const extractDoubleDollarMath = (text: string): string[] =>
  extractMathExpressions(text, { allowSingleDollar: false }).map(({ content }) => content);

/** Replaces expressions from right to left so original indices remain valid. */
export const replaceMathWithPlaceholders = (
  text: string,
  replacer: (content: string, delimiter: '$$' | '$', isBlock: boolean) => string,
): string => {
  let result = text;
  for (const expression of extractMathExpressions(text).reverse()) {
    result = `${result.slice(0, expression.start)}${replacer(expression.content, expression.delimiter, expression.isBlock)}${result.slice(expression.end)}`;
  }
  return result;
};

export const segmentDelimitedMath = (text: string, options?: MathExpressionOptions): DelimitedMathTextSegment[] => {
  const expressions = extractMathExpressions(text, options);
  if (expressions.length === 0) return [{ type: 'text', value: text }];

  const segments: DelimitedMathTextSegment[] = [];
  let cursor = 0;
  for (const expression of expressions) {
    if (expression.start > cursor) segments.push({ type: 'text', value: text.slice(cursor, expression.start) });
    segments.push({
      type: 'math',
      expression: expression.content,
      raw: text.slice(expression.start, expression.end),
      delimiter: expression.delimiter,
      display: expression.isBlock ? 'block' : 'inline',
    });
    cursor = expression.end;
  }
  if (cursor < text.length) segments.push({ type: 'text', value: text.slice(cursor) });
  return segments;
};
